import {ChangeEvent, KeyboardEvent, useEffect, useMemo, useState} from "react";
import {Input} from "@chakra-ui/react";
import {formatMillisecondsAsTimeCode} from "../../pages/cueList/cueListHelpers";

type Props = {
    valueMs?: number;
    onChangeMs: (valueMs: number) => void;
    onEmpty?: () => void;
    isDisabled?: boolean;
    width?: string;
    allowEmpty?: boolean;
    mixedValue?: boolean;
    mixedDisplay?: string;
};

export const parseTimeCodeToMilliseconds = (value: string): number | undefined => {
    const trimmed = value.trim();
    const match = /^(\d{1,2}):(\d{1,2}):(\d)$/.exec(trimmed);
    if (!match) {
        return undefined;
    }
    const minutes = parseInt(match[1], 10);
    const seconds = parseInt(match[2], 10);
    const tenths = parseInt(match[3], 10);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds) || !Number.isFinite(tenths)) {
        return undefined;
    }
    if (seconds < 0 || seconds > 59 || tenths < 0 || tenths > 9) {
        return undefined;
    }
    return (minutes * 60000) + (seconds * 1000) + (tenths * 100);
};

const autoFormatTimeCodeInput = (nextRaw: string, previousRaw: string) => {
    if (nextRaw.length < previousRaw.length) {
        return nextRaw;
    }
    const digitsOnly = nextRaw.replace(/:/g, "");
    if (!/^\d*$/.test(digitsOnly)) {
        return nextRaw;
    }
    if (!nextRaw.includes(":")) {
        if (digitsOnly.length <= 2) {
            return digitsOnly.length === 2 ? `${digitsOnly}:` : digitsOnly;
        }
        if (digitsOnly.length <= 4) {
            const minutes = digitsOnly.slice(0, 2);
            const seconds = digitsOnly.slice(2);
            return seconds.length === 2 ? `${minutes}:${seconds}:` : `${minutes}:${seconds}`;
        }
        const minutes = digitsOnly.slice(0, 2);
        const seconds = digitsOnly.slice(2, 4);
        const tenths = digitsOnly.slice(4, 5);
        return `${minutes}:${seconds}:${tenths}`;
    }
    if (/^\d{2}$/.test(nextRaw)) {
        return `${nextRaw}:`;
    }
    if (/^\d{2}:\d{2}$/.test(nextRaw)) {
        return `${nextRaw}:`;
    }
    return nextRaw;
};

const normalizeIncompleteTimeCode = (rawValue: string): string | undefined => {
    const trimmed = rawValue.trim();
    if (trimmed.length === 0) {
        return "";
    }

    const formatted = autoFormatTimeCodeInput(trimmed, "");
    if (!/^[0-9:]+$/.test(formatted)) {
        return undefined;
    }

    const parts = formatted.split(":");
    if (parts.length === 0 || parts.length > 3) {
        return undefined;
    }
    if (parts[0]?.length > 2 || parts[1]?.length > 2 || parts[2]?.length > 1) {
        return undefined;
    }

    const minutesPart = parts[0];
    if (!minutesPart || minutesPart.length === 0) {
        return undefined;
    }

    const minutes = parseInt(minutesPart.padStart(2, "0"), 10);
    const seconds = parseInt((parts[1] || "0").padStart(2, "0"), 10);
    const tenths = parseInt((parts[2] || "0").slice(0, 1), 10);

    if (!Number.isFinite(minutes) || !Number.isFinite(seconds) || !Number.isFinite(tenths)) {
        return undefined;
    }
    if (seconds < 0 || seconds > 59 || tenths < 0 || tenths > 9) {
        return undefined;
    }

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}:${tenths}`;
};

const getNextRightStepValue = (rawValue: string): string | undefined => {
    const trimmed = rawValue.trim();
    if (trimmed.length === 0) {
        return "0";
    }
    const current = autoFormatTimeCodeInput(trimmed, "");
    const completed = normalizeIncompleteTimeCode(current);
    if (!completed || current.length >= completed.length) {
        return undefined;
    }
    const nextRaw = `${current}${completed[current.length]}`;
    return autoFormatTimeCodeInput(nextRaw, current);
};

const isCompletableTimeCode = (rawValue: string) => {
    return normalizeIncompleteTimeCode(rawValue) !== undefined;
};

const TimeCodeInput = ({
    valueMs,
    onChangeMs,
    onEmpty,
    isDisabled,
    width = "110px",
    allowEmpty = false,
    mixedValue = false,
    mixedDisplay = "--:--:-"
}: Props) => {
    const [rawValue, setRawValue] = useState<string>(valueMs === undefined && allowEmpty ? "" : formatMillisecondsAsTimeCode(valueMs));
    const [isValid, setIsValid] = useState<boolean>(true);
    const normalized = useMemo(() => valueMs === undefined && allowEmpty ? "" : formatMillisecondsAsTimeCode(valueMs), [allowEmpty, valueMs]);

    useEffect(() => {
        if (mixedValue) {
            setRawValue(mixedDisplay);
            setIsValid(true);
            return;
        }
        setRawValue(normalized);
        setIsValid(true);
    }, [mixedDisplay, mixedValue, normalized]);

    const commitValue = (nextRaw: string) => {
        if (mixedValue && nextRaw.trim().length === 0) {
            setRawValue(mixedDisplay);
            setIsValid(true);
            return;
        }
        const normalizedRaw = normalizeIncompleteTimeCode(nextRaw);
        if (normalizedRaw === "") {
            if (allowEmpty) {
                setRawValue("");
                setIsValid(true);
                onEmpty && onEmpty();
                return;
            }
            // Default behavior: empty input commits to zero.
            setRawValue(formatMillisecondsAsTimeCode(0));
            setIsValid(true);
            onChangeMs(0);
            return;
        }
        if (!normalizedRaw) {
            setRawValue(normalized);
            setIsValid(true);
            return;
        }
        const nextMs = parseTimeCodeToMilliseconds(normalizedRaw);
        if (nextMs === undefined) {
            setRawValue(normalized);
            setIsValid(true);
            return;
        }
        setIsValid(true);
        setRawValue(formatMillisecondsAsTimeCode(nextMs));
        onChangeMs(nextMs);
    };

    return (
        <Input
            size={"sm"}
            width={width}
            value={rawValue}
            placeholder={"00:00:0"}
            onFocus={(event) => {
                if (mixedValue && rawValue === mixedDisplay) {
                    setRawValue("");
                    setIsValid(true);
                    return;
                }
                event.target.select();
            }}
            isDisabled={isDisabled}
            style={{color: isValid ? "black" : "red"}}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const nextRaw = autoFormatTimeCodeInput(event.target.value, rawValue);
                setRawValue(nextRaw);
                if (nextRaw.trim().length === 0) {
                    setIsValid(allowEmpty);
                    return;
                }
                setIsValid(isCompletableTimeCode(nextRaw));
            }}
            onBlur={(event: ChangeEvent<HTMLInputElement>) => {
                commitValue(event.target.value);
            }}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "ArrowRight") {
                    const caretStart = event.currentTarget.selectionStart;
                    const caretEnd = event.currentTarget.selectionEnd;
                    const isCaretAtEnd =
                        caretStart !== null &&
                        caretEnd !== null &&
                        caretStart === event.currentTarget.value.length &&
                        caretEnd === event.currentTarget.value.length;
                    if (!isCaretAtEnd) {
                        return;
                    }
                    const nextValue = getNextRightStepValue(event.currentTarget.value);
                    if (nextValue) {
                        event.preventDefault();
                        setRawValue(nextValue);
                        if (nextValue.trim().length === 0) {
                            setIsValid(allowEmpty);
                            return;
                        }
                        setIsValid(isCompletableTimeCode(nextValue));
                        return;
                    }
                }
                if (event.key === "Escape") {
                    event.preventDefault();
                    if (mixedValue) {
                        setRawValue(mixedDisplay);
                    } else {
                        setRawValue(normalized);
                    }
                    setIsValid(true);
                    return;
                }
                if (event.key === "Enter") {
                    commitValue(event.currentTarget.value);
                    event.currentTarget.blur();
                }
            }}
        />
    );
};

export default TimeCodeInput;
